/**
 * Video Editor — background-music picker.
 *
 * Sits on the Storyboard surface: the operator picks a CC0 library track for
 * the cut and chooses how its audio is handled — music ducked under the clips'
 * own audio ('mix'), or music only with the clips silenced ('only'). The
 * selection persists on `project.music`; the pipeline copies the track into
 * the render input dir and appends the music directive to the brief.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { VideoProject, MusicTrack, AudioMode } from '../types';
import type { VideoEditorApi } from '../videoEditorApi';

interface Props {
  project: VideoProject;
  api: VideoEditorApi;
  onRefresh: () => Promise<any>;
}

function mmss(sec: number): string {
  if (!sec || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const MusicPicker: React.FC<Props> = ({ project, api, onRefresh }) => {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Inline preview — one reused <audio>; previewId is the playing track.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const selected = project.music || {};
  const selectedId = selected.trackId || null;
  const audioMode: AudioMode = selected.audioMode === 'only' ? 'only' : 'mix';

  useEffect(() => {
    let alive = true;
    api.listMusic()
      .then(t => { if (alive) setTracks(t); })
      .catch(() => { if (alive) setErr('Could not load the music library'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  // Revoke any outstanding preview object URL on unmount.
  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const stopPreview = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setPreviewId(null);
  }, []);

  const togglePreview = useCallback(async (track: MusicTrack) => {
    if (previewId === track.id) { stopPreview(); return; }
    stopPreview();
    try {
      const url = await api.loadMusicPreviewUrl(track.file);
      urlRef.current = url;
      setPreviewId(track.id);
      const el = audioRef.current;
      if (el) { el.src = url; el.play().catch(() => {}); }
    } catch {
      setErr('Could not play that preview');
    }
  }, [api, previewId, stopPreview]);

  async function applySelection(trackId: string | null, mode: AudioMode) {
    try {
      setBusy(true); setErr(null);
      await api.setProjectMusic(project._id, trackId, mode);
      await onRefresh();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Could not update the soundtrack');
    } finally { setBusy(false); }
  }

  // Selecting the already-selected track toggles it off.
  const pick = (track: MusicTrack) =>
    applySelection(track.id === selectedId ? null : track.id, audioMode);

  return (
    <div className="ve-panel">
      <h3>Soundtrack <span className="ve-muted">— optional background music</span></h3>
      {err && <div className="ve-banner ve-banner--error">{err}</div>}

      {loading && <p className="ve-muted">Loading the music library…</p>}
      {!loading && tracks.length === 0 && !err && (
        <p className="ve-muted">No music tracks available.</p>
      )}

      {!loading && tracks.length > 0 && (
        <>
          <div className="ve-music__list">
            {tracks.map(t => {
              const isSel = t.id === selectedId;
              const isPlaying = previewId === t.id;
              return (
                <div className={`ve-music__track${isSel ? ' ve-music__track--sel' : ''}`} key={t.id}>
                  <button className="ve-music__play"
                    title={isPlaying ? 'Stop preview' : 'Preview'}
                    onClick={() => togglePreview(t)}>{isPlaying ? '◼' : '▶'}</button>
                  <div className="ve-music__info">
                    <div className="ve-music__title">{t.title}</div>
                    <div className="ve-music__meta">{t.mood} · {mmss(t.durationSec)}</div>
                  </div>
                  <button className={`ve-btn${isSel ? ' ve-btn--primary' : ''}`}
                    disabled={busy} onClick={() => pick(t)}>
                    {isSel ? 'Selected' : 'Select'}
                  </button>
                </div>
              );
            })}
          </div>

          {selectedId && (
            <div className="ve-music__mode">
              <span className="ve-muted">Audio:</span>
              <button className={`ve-chip${audioMode === 'mix' ? ' ve-chip--active' : ''}`}
                disabled={busy} onClick={() => applySelection(selectedId, 'mix')}>
                Music under clip audio
              </button>
              <button className={`ve-chip${audioMode === 'only' ? ' ve-chip--active' : ''}`}
                disabled={busy} onClick={() => applySelection(selectedId, 'only')}>
                Music only (mute clips)
              </button>
            </div>
          )}

          <div className="ve-music__foot">
            {selectedId
              ? <span>Selected: <strong>{selected.title}</strong>{' '}
                  <button className="ve-link" disabled={busy}
                    onClick={() => applySelection(null, 'mix')}>remove</button></span>
              : <span className="ve-muted">No music — the video uses the clips' own audio.</span>}
          </div>
          <p className="ve-music__note ve-muted">
            All tracks are CC0 (public domain). “Music only” drops the clips’ own
            audio. The pipeline applies the soundtrack on the next render.
          </p>
        </>
      )}
      <audio ref={audioRef} onEnded={stopPreview} hidden />
    </div>
  );
};

export default MusicPicker;
